import { useState } from "react";
import { useParams } from "react-router";
import { DELEGABLE_TIERS, type ActorDTO, type Tier } from "@roomate/core";
import { ApiError, useActors, useMe, useSetDelegationPolicy } from "../api.ts";
import { Avatar, Bento, Block, Disclosure, Page, PageHeader, Pill, Switch, Tile } from "../calm.tsx";
import { CorrectionRules } from "../components/CorrectionRules.tsx";
import { RestoreMemberButton } from "../components/MemberArchive.tsx";
import { ActorName, Button, cx, ErrorNote, inputCls } from "../ui.tsx";
import { Field } from "./Project.tsx";
import {
  draftFromPolicy,
  groupProblems,
  policyFromDraft,
  splitServerProblems,
  validateDraft,
  type DelegationDraft,
} from "../lib/delegation.ts";

export function humanRoutingStatus(actor: Pick<ActorDTO, "active" | "access" | "canBuild" | "archived">): string {
  if (actor.archived) return "Removed from the room. Past work still shows their name.";
  if (!actor.active) return "Not available for routing or reviews.";
  if (actor.access === "viewer") return "Viewer access only.";
  if (actor.canBuild === false) return "Available for reviews; opted out of builds.";
  return "Available for routing and reviews.";
}

export function ProfilePage() {
  const { actorId = "" } = useParams();
  const actorsQuery = useActors();
  const actors = actorsQuery.data ?? [];
  const me = useMe().data;
  const actor = actors.find((a) => a.id === actorId);

  if (!me || actorsQuery.isLoading) {
    return (
      <Page>
        <p className="text-ink-3">Loading…</p>
      </Page>
    );
  }
  if (!actor) {
    return (
      <Page>
        <p className="text-ink-3">No one here with that id.</p>
      </Page>
    );
  }

  const canEditPolicy = actor.kind === "human" && (actor.id === me.id || me.access === "admin");

  return (
    <Page>
      <PageHeader
        crumbs={[{ label: "The room", to: "/team" }, { label: actor.name }]}
        title={actor.name}
        meta={
          <span className="text-ink-2">
            {actor.kind === "agent" ? (
              <>
                {actor.runtime} · owned by <ActorName id={actor.ownerId} actors={actors} />
              </>
            ) : (
              <>
                {actor.role.replace("_", " ")} · {actor.access}
              </>
            )}
          </span>
        }
        actions={<Avatar name={actor.name} kind={actor.kind} size={56} live={actor.online} />}
      />

      <div className="mt-8 space-y-8">
        <Block title="Details">
          <Bento>
            <Tile span={3} title="Skills">
              {actor.skills.length === 0 ? (
                <p className="text-[13.5px] text-ink-3">No skills set: routing treats every task as a stretch.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {actor.skills.map((s) => (
                    <Pill key={s.key}>
                      {s.key} · {s.level}
                    </Pill>
                  ))}
                </div>
              )}
              {actor.focus.length > 0 && <p className="mt-3 text-[13px] text-ink-2">focus: {actor.focus.join(", ")}</p>}
            </Tile>
            <Tile span={3} title={actor.kind === "agent" ? "Concurrency" : "Status"}>
              {actor.kind === "human" ? (
                <p className="text-[13.5px] text-ink-2">{humanRoutingStatus(actor)}</p>
              ) : (
                <p className="text-[13.5px] text-ink-2">{actor.maxParallel === 0 ? "review only, never builds" : `up to ${actor.maxParallel} task${actor.maxParallel === 1 ? "" : "s"} at once`}</p>
              )}
              {actor.allowedAreas.length > 0 && (
                <Disclosure summary={`${actor.allowedAreas.length} allowed area${actor.allowedAreas.length === 1 ? "" : "s"}`} className="mt-3">
                  <ul className="space-y-0.5 font-mono text-[11.5px] text-ink-2">
                    {actor.allowedAreas.map((a) => (
                      <li key={a}>{a}</li>
                    ))}
                  </ul>
                </Disclosure>
              )}
              {!actor.active && !actor.archived && <p className="mt-3 text-[13px] text-high">Deactivated</p>}
              {actor.archived && me.access === "admin" && <RestoreMemberButton actor={actor} />}
            </Tile>
          </Bento>
        </Block>

        {actor.kind === "agent" && <CorrectionRules agentId={actor.id} actors={actors} />}

        {actor.kind === "human" && (
          <Block title="Reviews" aside={canEditPolicy ? undefined : "read-only"} testId="reviews-panel">
            {/* Keyed by actor: the draft lives in useState, so without a remount an admin moving
                from /team/niv to /team/erez would keep Niv's draft and one Save would overwrite
                Erez's policy with it. */}
            {canEditPolicy ? (
              <ReviewsPanelEditable key={actor.id} actor={actor} actors={actors} />
            ) : (
              <ReviewsPanelReadOnly actor={actor} actors={actors} />
            )}
          </Block>
        )}
      </div>
    </Page>
  );
}

function ReviewsPanelReadOnly({ actor, actors }: { actor: ActorDTO; actors: ActorDTO[] }) {
  const policy = actor.delegationPolicy;
  if (!policy) return <p className="text-[13.5px] text-ink-3" data-testid="delegate-readonly">Delegation is off.</p>;
  const agentName = actors.find((a) => a.id === policy.agentId)?.name ?? policy.agentId;
  return (
    <div className="space-y-1.5 text-[13.5px] text-ink-2" data-testid="delegate-readonly">
      <p>
        Delegates to <span className="font-medium text-ink">{agentName}</span>
      </p>
      <p>
        Tiers: {policy.tiers.join(", ")} <span className="text-ink-3">(critical always needs a person)</span>
      </p>
      <p>Areas: {policy.areas.length ? policy.areas.join(", ") : "any area, unless a project rule holds it back"}</p>
      <p>Spot-checks: {policy.kinds.includes("spot_check") ? "included" : "not included"}</p>
    </div>
  );
}

function ReviewsPanelEditable({ actor, actors }: { actor: ActorDTO; actors: ActorDTO[] }) {
  const [draft, setDraft] = useState<DelegationDraft>(() => draftFromPolicy(actor.delegationPolicy));
  const mutate = useSetDelegationPolicy();
  const myAgents = actors.filter((a) => a.kind === "agent" && a.active && a.ownerId === actor.id);

  const onChange = (patch: Partial<DelegationDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    // Any edit invalidates both a stale error and a stale "Saved" badge — the draft no longer
    // matches what was last saved (or last rejected), so neither should keep showing.
    if (mutate.isError || mutate.isSuccess) mutate.reset();
  };

  // Inline validation reuses validateDelegationPolicy from @roomate/core (no second copy of the
  // rules here), so a policy naming an agent the actor doesn't own is rejected before Save is even
  // enabled — no request goes out for it.
  const clientProblems = validateDraft(draft, { actor, agents: actors });
  const serverProblems =
    mutate.isError && mutate.error instanceof ApiError ? splitServerProblems(mutate.error.message) : mutate.isError ? [String((mutate.error as Error).message)] : [];
  const problems = groupProblems([...clientProblems, ...serverProblems]);

  const save = () => mutate.mutate({ id: actor.id, policy: policyFromDraft(draft) });

  return (
    <div className="space-y-4">
      <Switch checked={draft.enabled} onChange={(v) => onChange({ enabled: v })} label="Delegate my reviews to my agent" testId="delegate-switch" />

      {draft.enabled && (
        <div className="space-y-4 border-t border-rule/60 pt-4">
          <Field label="Agent">
            {myAgents.length === 0 ? (
              <p className="text-[13px] text-ink-3" data-testid="delegate-agent-empty">
                You don't own any active agents yet, so there's nothing to delegate to.
              </p>
            ) : (
              <select className={inputCls} value={draft.agentId} onChange={(e) => onChange({ agentId: e.target.value })} data-testid="delegate-agent-select">
                <option value="">Choose an agent…</option>
                {myAgents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            )}
            {problems.agentId.length > 0 && <ErrorNote error={problems.agentId.join("; ")} />}
          </Field>

          <Field label="Tiers it may decide alone">
            <div className="flex flex-wrap items-center gap-4">
              {DELEGABLE_TIERS.map((t: Tier) => (
                <label key={t} className="inline-flex items-center gap-1.5 text-[13px] capitalize">
                  <input
                    type="checkbox"
                    checked={draft.tiers.includes(t)}
                    onChange={(e) => onChange({ tiers: e.target.checked ? [...draft.tiers, t] : draft.tiers.filter((x) => x !== t) })}
                    data-testid={`delegate-tier-${t}`}
                  />
                  {t}
                </label>
              ))}
              <label className="inline-flex items-center gap-1.5 text-[13px] text-ink-3" title="always needs a person">
                <input type="checkbox" checked={false} disabled data-testid="delegate-tier-critical" />
                critical <span className="text-[11.5px]">(always needs a person)</span>
              </label>
            </div>
            {problems.tiers.length > 0 && <ErrorNote error={problems.tiers.join("; ")} />}
          </Field>

          <Field label="Areas" hint="globs, comma-separated — empty = any area, unless a project rule holds it back">
            <input
              className={cx(inputCls, "font-mono")}
              value={draft.areasText}
              onChange={(e) => onChange({ areasText: e.target.value })}
              placeholder="apps/web/**"
              data-testid="delegate-areas"
            />
          </Field>

          <label className="flex items-start gap-2 text-[13px]">
            <input type="checkbox" className="mt-0.5" checked={draft.spotCheck} onChange={(e) => onChange({ spotCheck: e.target.checked })} data-testid="delegate-spot-check" />
            <span>
              Include spot-checks
              <span className="block text-[12px] text-ink-3">Spot-checks exist to keep your trust in agents calibrated.</span>
            </span>
          </label>

          <div className="flex items-center gap-3">
            <Button variant="primary" onClick={save} disabled={clientProblems.length > 0 || mutate.isPending} data-testid="delegate-save">
              Save
            </Button>
            {mutate.isSuccess && <span className="text-[12.5px] text-[color:var(--ok)]">Saved</span>}
          </div>
          {problems.general.length > 0 && <ErrorNote error={problems.general.join("; ")} />}
        </div>
      )}

      {!draft.enabled && (
        <div className="flex items-center gap-3 border-t border-rule/60 pt-4">
          <Button variant="primary" onClick={save} disabled={mutate.isPending} data-testid="delegate-save">
            Save
          </Button>
          {mutate.isSuccess && <span className="text-[12.5px] text-[color:var(--ok)]">Saved</span>}
        </div>
      )}
    </div>
  );
}
